import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Checkbox, Input, Radio, Select, Space, Tag, Typography } from "@arco-design/web-react";
import { IconArrowDown, IconArrowUp, IconDelete } from "@arco-design/web-react/icon";
import { FetchApi } from "../Api/AuthenticatedApi";
import useRecordTypes from "../Components/CustomFields/useRecordTypes";
import { visibleFields } from "../Components/CustomFields/CustomFieldInputs";

const { Text } = Typography;

// Built-in contact fields a form can collect (submission currently persists these).
const BUILTINS = [
  { source: "builtin", key: "email", label: "Email" },
  { source: "builtin", key: "name", label: "Name" },
];

// Configures which fields a campaign form collects: a contact type + a list of
// fields, each Visible (collected from the visitor) or Preset (hidden, a fixed
// builder-set value). Email is locked visible + required.
export default function FormBuilder({ typeId, setTypeId, fields, setFields, disabled }) {
  const { getAccessTokenSilently } = useAuth0();
  const { types } = useRecordTypes("contact");
  const [cfDefs, setCfDefs] = useState([]);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=contact",
      (data) => setCfDefs(data || []), () => {}, () => {});
  }, [getAccessTokenSilently]);

  const added = new Set(fields.map((f) => f.source + ":" + f.key));
  const available = [
    ...BUILTINS,
    ...visibleFields(cfDefs, typeId).map((d) => ({ source: "custom", key: d.key, label: d.label })),
  ].filter((f) => !added.has(f.source + ":" + f.key));

  const addField = (composite) => {
    const opt = available.find((a) => a.source + ":" + a.key === composite);
    if (!opt) return;
    setFields([...fields, { source: opt.source, key: opt.key, label: opt.label, mode: "visible", required: opt.key === "email", value: "" }]);
  };
  const updateField = (idx, patch) => setFields(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeField = (idx) => setFields(fields.filter((_, i) => i !== idx));
  // Swaps a field with its neighbour; the form shows fields in this order.
  const moveField = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= fields.length) return;
    const next = [...fields];
    [next[idx], next[to]] = [next[to], next[idx]];
    setFields(next);
  };

  return (
    <div>
      {types.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Creates contacts of type</Text></div>
          <Select
            allowClear
            placeholder="No type"
            disabled={disabled}
            style={{ width: 260 }}
            value={typeId || undefined}
            onChange={(v) => setTypeId(v || "")}
            options={types.map((t) => ({ label: t.name, value: t.id }))}
          />
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            scopes which custom fields you can collect
          </Text>
        </div>
      )}

      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {fields.map((f, idx) => {
          const locked = f.key === "email";
          return (
            <div
              key={f.source + ":" + f.key}
              style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 10px", border: "1px solid var(--color-border-2)", borderRadius: 6, background: "var(--color-fill-1)" }}
            >
              <div style={{ minWidth: 150 }}>
                <Text style={{ fontWeight: 500 }}>{f.label}</Text>{" "}
                {f.source === "builtin" && <Tag size="small">built-in</Tag>}
              </div>
              <Radio.Group
                type="button"
                size="small"
                disabled={disabled}
                value={f.mode}
                onChange={(v) => updateField(idx, { mode: v })}
                options={[{ label: "Visible", value: "visible" }, { label: "Preset", value: "preset" }]}
              />
              {f.mode === "preset" ? (
                <Input size="small" style={{ width: 180 }} placeholder="Preset value (hidden)" disabled={disabled} value={f.value} onChange={(v) => updateField(idx, { value: v })} />
              ) : (
                <Checkbox disabled={disabled || locked} checked={f.required} onChange={(v) => updateField(idx, { required: v })}>Required</Checkbox>
              )}
              <div style={{ flexGrow: 1 }} />
              <Button size="small" type="text" shape="circle" icon={<IconArrowUp />} aria-label="Move up" disabled={disabled || idx === 0} onClick={() => moveField(idx, -1)} />
              <Button size="small" type="text" shape="circle" icon={<IconArrowDown />} aria-label="Move down" disabled={disabled || idx === fields.length - 1} onClick={() => moveField(idx, 1)} />
              <Button size="small" type="text" shape="circle" icon={<IconDelete />} disabled={disabled || locked} onClick={() => removeField(idx)} />
            </div>
          );
        })}
      </Space>

      {available.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Select
            placeholder="Add a field…"
            disabled={disabled}
            style={{ width: 240 }}
            value={undefined}
            onChange={addField}
            options={available.map((a) => ({ label: a.label, value: a.source + ":" + a.key }))}
          />
        </div>
      )}
    </div>
  );
}
